import { format } from "date-fns";
import { nb } from "date-fns/locale";
import { CalendarDays } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export interface DateTimeInputProps {
  /** Selected date */
  date?: Date;
  onDateChange?: (date: Date | undefined) => void;
  /** Show date picker (default true) */
  showDate?: boolean;
  /** Time value as HH:MM */
  time?: string;
  onTimeChange?: (time: string) => void;
  /** Show time input (default true) */
  showTime?: boolean;
  /** Label for time field */
  timeLabel?: string;
  /** Disable dates before today */
  disablePast?: boolean;
  /** Second time field (end time) */
  endTime?: string;
  onEndTimeChange?: (time: string) => void;
  showEndTime?: boolean;
}

export function DateTimeInput({
  date,
  onDateChange,
  showDate = true,
  time,
  onTimeChange,
  showTime = true,
  timeLabel = "Klokkeslett",
  disablePast = false,
  endTime,
  onEndTimeChange,
  showEndTime = false,
}: DateTimeInputProps) {
  return (
    <div className="grid grid-cols-[1fr_auto_auto] items-end gap-2">
      {showDate && (
        <div className="min-w-0 space-y-1.5">
          <Label className="text-sm">Dato</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  "w-full justify-start text-left font-normal gap-2",
                  !date && "text-muted-foreground"
                )}
              >
                <CalendarDays className="h-4 w-4" />
                {date
                  ? format(date, "EEE d. MMM", { locale: nb })
                  : "Velg dato"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={onDateChange}
                disabled={
                  disablePast
                    ? (d) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return d < today;
                      }
                    : undefined
                }
                initialFocus
                className={cn("p-3 pointer-events-auto")}
              />
            </PopoverContent>
          </Popover>
        </div>
      )}

      {showTime && (
        <div className="space-y-1.5 shrink-0">
          <Label className="text-sm">{showEndTime ? "Start" : timeLabel}</Label>
          <Input
            type="time"
            value={time ?? ""}
            onChange={(e) => onTimeChange?.(e.target.value)}
            className="w-[5.5rem]"
          />
        </div>
      )}

      {showEndTime && (
        <div className="space-y-1.5 shrink-0">
          <Label className="text-sm">Slutt</Label>
          <Input
            type="time"
            value={endTime ?? ""}
            onChange={(e) => onEndTimeChange?.(e.target.value)}
            className="w-[5.5rem]"
          />
        </div>
      )}
    </div>
  );
}
